import React, {FC} from "react";
import {T, useTranslate} from "@tolgee/react";
import {Box, Button, Typography} from "@material-ui/core";
import {useForm} from "react-hook-form";
import Form, {BasicInput, ActionButton} from "view/components/Form";
import FfairModal from "view/components/modal/FfairModal";
import FfairPaper from "view/components/FfairPaper";
import useStyles from "./styles";

type FormData = {
  password: string;
};

type Props = {
  title?: string;
  subtitle?: string;
  description?: React.ReactNode;
  submitTitle?: string;
  onClose: () => void;
  onConfirm: (password: string) => Promise<void>;
};

const ConfirmPasswordModal: FC<Props> = ({
  title,
  subtitle,
  description,
  submitTitle,
  onClose,
  onConfirm,
}) => {
  const classes = useStyles();
  const {t} = useTranslate();
  const form = useForm<FormData>({mode: "all", defaultValues: {password: ""}});
  const {
    handleSubmit,
    setError,
    formState: {isSubmitting},
  } = form;

  const resolvedTitle = title ?? t("common.two_factor.confirm_password.title");
  const resolvedSubmitTitle = submitTitle ?? t("common.action.submit");

  const onSubmit = async ({password}: FormData) => {
    try {
      await onConfirm(password);
    } catch (err) {
      const status = (err as {response?: {status?: number}})?.response?.status;
      setError("password", {
        type: "manual",
        message:
          status === 400 || status === 401
            ? t("common.two_factor.incorrect_password")
            : t("common.error.try_again"),
      });
    }
  };

  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  return (
    <FfairModal open onClose={handleClose} isCalledByAnotherModal>
      <FfairPaper className={classes.root}>
        <Box className={classes.header}>
          {subtitle && (
            <Typography variant="body2" className={classes.subtitle}>
              {subtitle}
            </Typography>
          )}
          <Typography variant="h2" className={classes.title}>
            {resolvedTitle}
          </Typography>
        </Box>
        {description && (
          <Typography className={classes.description}>{description}</Typography>
        )}
        <Form form={form} onSubmit={handleSubmit(onSubmit)} noDefaultStyles>
          <BasicInput
            name="password"
            label={t("common.field.password")}
            type="password"
            className={classes.field}
            rules={{required: true}}
            showHelperText
            autoFocus
            disabled={isSubmitting}
          />
          <Box display="flex" justifyContent="flex-end" pt={3}>
            <Box pr={1}>
              <Button
                color="primary"
                disabled={isSubmitting}
                onClick={handleClose}
              >
                <T keyName="common.action.cancel" />
              </Button>
            </Box>
            <ActionButton
              type="submit"
              variant="contained"
              color="primary"
              text={resolvedSubmitTitle}
              showLoader
            />
          </Box>
        </Form>
      </FfairPaper>
    </FfairModal>
  );
};

export default ConfirmPasswordModal;
